import './index.css';
import { Header, ScreenBackground, View, Text, RouteWrapper } from '../../../component';
import { useRouteLocation, useLink } from 'react-router-stack';
import useTheme from '../../../utilities/hooks/theme';
import { useDispatch, useSelector } from 'react-redux';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import moment from 'moment';
import { useState, useRef, useEffect, ChangeEvent } from 'react';
import { showToast, showErrorToast } from '../../../actions/app-actions-types';
import { editUserProfile, updateUserData } from '../../../actions/user-actions-types';
import I18n from '../../../utilities/locale';
import Regex from '../../../utilities/Regex';
import TextField from '@mui/material/TextField/TextField';
import DatePicker from '@mui/lab/DatePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import RadioGroup from '@mui/material/RadioGroup/RadioGroup';
import Radio from '@mui/material/Radio/Radio';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Button from '@mui/material/Button/Button';
import { isOverEighteen } from '../../../utilities/helper';

const EditProfile = () => {
  const { MoreTheme: theme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useLink();
  const { state = {} } = useRouteLocation();
  let minDOB = new Date();
  minDOB.setMonth(minDOB.getMonth() - 12 * 10);
  const { PANCardStatus = '' } = state;
  const user = useSelector((state: any) => state.user);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDOB] = useState(null as any);
  const [gender, setGender] = useState('male');

  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [dobError, setDOBError] = useState(null);
  const [genderError, setGenderError] = useState(null);

  const refFirstName = useRef(null);
  const refLastName = useRef(null);
  const refDOB = useRef(null);

  useEffect(() => {
    const { firstName = '', lastName = '', dob: userDob, gender = '' } = user;
    setFirstName(firstName);
    setLastName(lastName);
    if (userDob) setDOB(moment(userDob).format('YYYY-MM-DD'));
    if (gender) setGender(gender);
  }, [user]);

  const onPressUpdateDetails = () => {
    let firstNameError = null,
      lastNameError = null,
      dobError = null,
      error = false;
    const formattedDateDOB: any = moment(dob).format('YYYY-MM-DD');
    if (!firstName) {
      firstNameError = I18n.t('FirstNameMissing');
      error = true;
    }

    if (!lastName) {
      lastNameError = I18n.t('LastNameMissing');
      error = true;
    }

    if (!error && gender === '') {
      dispatch(showErrorToast(I18n.t('SelectGender')));
      return;
    }
    if (!formattedDateDOB || !dob) {
      dobError = I18n.t('MissingDOB');
      error = true;
    } else if (!Regex.validateDate(formattedDateDOB)) {
      dobError = I18n.t('InValidDate');
    } else if (!isOverEighteen(formattedDateDOB)) {
      dobError = I18n.t('userMustBe18Plus');
    }

    if (error) {
      setFirstNameError(firstNameError);
      setLastNameError(lastNameError);
      setDOBError(dobError);
      if (dob === null) {
        setDOB('');
      }
    } else {
      updateUserDetailProceed(formattedDateDOB);
    }
  };

  const updateUserDetailProceed = (dob: any) => {
    const data = {
      firstname: firstName.trim(),
      lastname: lastName.trim(),
      gender,
      dob
    };
    dispatch(
      editUserProfile({
        data,
        callback: ({ error }: { error: any }) => {
          if (!error) {
            dispatch(showToast(I18n.t('profileUpdatedSuccessfully')));
            dispatch(
              updateUserData({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                gender,
                dob
              })
            );
            navigate.go(-1);
          }
        }
      })
    );
  };

  const onChangeFirstName = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateName(event.target.value) || event.target.value === '') {
      setFirstName(event.target.value);
      if (firstNameError != null) setFirstNameError(null);
    }
  };

  const onChangeLastName = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateName(event.target.value) || event.target.value === '') {
      setLastName(event.target.value);
      if (lastNameError != null) setLastNameError(null);
    }
  };

  const handleDatePicked = (date: Date | null | undefined) => {
    setDOB(date);
    if (dobError != null) setDOBError(null);
  };

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setGender((event.target as HTMLInputElement).value);
    if (genderError != null) {
      setGenderError(null);
    }
  };

  const labelColor = {
    color: theme.editProfileLabel
  };

  const buttonColor = {
    color: theme.editProfileSubmitText,
    backgroundColor: theme.editProfileSubmitBtn
  };

  const inputColor = {
    backgroundColor: theme.editProfileTextInputBG,
    color: theme.editProfileTextInputColor
  };

  const inputSx: SxProps<Theme> = {
    '& .MuiInputBase-input': {
      color: theme.editProfileTextInputColor,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'uppercase'
    },
    '& .MuiFormHelperText-root.Mui-error': {
      backgroundColor: theme.editProfileTextInputErrorBG,
      margin: 0,
      color: theme.error,
      paddingLeft: '5px',
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root': {
      color: theme.editProfileTextInputColor,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.editProfileTextInputColor,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& input::placeholder': {
      color: theme.editProfileTextInputPlaceHolder,
      fontSize: '1.1rem',
      textTransform: 'none'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.editProfileTextInputColor
      },
      '&:hover fieldset': {
        borderColor: theme.editProfileTextInputColor
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.editProfileTextInputColor,
        borderWidth: '1px'
      }
    }
  };
  const radiosSx: SxProps<Theme> = {
    '& .MuiTypography-root': {
      fontSize: 'small',
      fontFamily: 'ProximaNova-Regular',
      color: theme.editProfileLabel
    }
  };

  const onClickBack = () => {
    try {
      navigate.goBackView();
    } catch (err) {
      navigate.go(-1);
    }
  };

  return (
    <RouteWrapper>
      <ScreenBackground>
        <Header onClickBack={onClickBack} title={'Edit Profile'} />
        <View className={'edit-profile-container'}>
          <TextField
            ref={refFirstName}
            style={inputColor}
            placeholder={'Enter Your First Name'}
            value={firstName}
            className={'edit-profile-input'}
            label={'First Name'}
            variant="outlined"
            size={'small'}
            onChange={onChangeFirstName}
            sx={inputSx}
            error={firstNameError ? true : false}
            helperText={firstNameError}
          />
          <TextField
            ref={refLastName}
            style={inputColor}
            placeholder={'Enter Your Last Name'}
            value={lastName}
            className={'edit-profile-input'}
            label={'Last Name'}
            variant="outlined"
            size={'small'}
            onChange={onChangeLastName}
            sx={inputSx}
            error={lastNameError ? true : false}
            helperText={lastNameError}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              ref={refDOB}
              disabled={PANCardStatus === 'verified'}
              label={I18n.t('DateOfBirth') + ' (YYYY-MM-DD)'}
              inputFormat="yyyy-MM-dd"
              value={dob}
              onChange={handleDatePicked}
              maxDate={minDOB}
              renderInput={(params: any) => (
                <TextField
                  placeholder={I18n.t('DateOfBirth') + ' (YYYY-MM-DD)'}
                  className={'edit-profile-input'}
                  style={inputColor}
                  size="small"
                  sx={inputSx}
                  error={dobError ? true : false}
                  helperText={dobError}
                  {...params}
                />
              )}
            />
          </LocalizationProvider>
          <View className={'edit-profile-gender-container'}>
            <Text style={labelColor} className={'edit-profile-gender-label'}>
              {'Gender'}
            </Text>
            <RadioGroup
              sx={radiosSx}
              defaultValue="male"
              row
              value={gender ? gender.toLowerCase() : 'male'}
              onChange={handleRadioChange}>
              <FormControlLabel value="male" control={<Radio />} label="Male" />
              <FormControlLabel value="female" control={<Radio />} label="Female" />
            </RadioGroup>
            <Text>{genderError}</Text>
          </View>
          <Button
            onClick={onPressUpdateDetails}
            className={'edit-profile-button '}
            style={buttonColor}>
            {'Update Profile'}
          </Button>
        </View>
      </ScreenBackground>
    </RouteWrapper>
  );
};
export default EditProfile;
